import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import "./dynamicCheckService.css";
import "react-phone-number-input/style.css";
import { Link, } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useLoading } from "../../_shared/LoadingContext";
import { toast } from "react-toastify";
import ReCAPTCHA from "react-google-recaptcha";
import Axios from "axios";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { getFreeSampale } from "../../_services/checkService";
import cookies from "js-cookie";
import checkServiceEn from '../../translation/checkServices-en.json'
import checkServiceEs from '../../translation/checkServices-es.json'
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useNavigate } from 'react-router-dom';
import httpRequest from '../../_services/httpServices'
import ServicePromotionVideos from "../../shared/videos/ServicePromotionVideos";
// Load Stripe outside the component to avoid reinitializing it on every render
// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_TEST_KEY);
const SITE_KEY = process.env.REACT_APP_SITE_KEY;

const DynamicCheckService = () => {
  const { t } = useTranslation();
  const [currentLanguageCode, setCurrentLanguageCode] = useState(cookies.get("i18next") || "en");
  const { checkService } = useParams();
  const [clientSecret, setClientSecret] = useState(null);
  const [freeSample, setFreeSample] = useState(null);
  const [show, setShow] = useState(false);
  const [valid_token, setValidToken] = useState([]);
  const navigate = useNavigate();

  const { startLoading, stopLoading } = useLoading();
  const captchaRef = useRef(null);
  // State to force re-rendering of the ReCAPTCHA component
  const [captchaKey, setCaptchaKey] = useState(Date.now()); // Use timestamp as a key for re-rendering
  // Reload captcha when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      const newLanguageCode = cookies.get("i18next") || "en";
      if (newLanguageCode !== currentLanguageCode) {
        setCurrentLanguageCode(newLanguageCode);
        // Change the key to remount ReCAPTCHA
        setCaptchaKey(Date.now());
      }
    };
    // Listen for cookie changes or language changes (you could also use another event)
    const intervalId = setInterval(handleLanguageChange, 1000); // Polling for language changes (or use other detection)
    return () => clearInterval(intervalId); // Cleanup the interval on component unmount
  }, [currentLanguageCode]);
  const services = currentLanguageCode === 'en' ? checkServiceEn : checkServiceEs;

  const serviceData = useMemo(() => {
    return services[checkService] || {
      url: "premium-blacklist-checker",
      provider: "DashServices",
      serviceId: 3,
      price: 3.99,
      title: "Blacklist check",
      subtitle: "Verify if your phone is blacklisted or clean using its IMEI number. Ensure your device is not blacklisted due to loss, theft, or unpaid bills."

    };
  }, [checkService, services]);
  useEffect(() => {
    if (serviceData) {
      setShow(true)
      const fetchData = async () => {
        try {
          const body = {
            serviceId: serviceData.serviceId,
            provider: serviceData.provider,
            imei: '000000000000000'
          }
          const response = await getFreeSampale(body)
          if (response) {
            setFreeSample(response?.result)
          }
        } catch (err) {
          console.error('API call failed:', err);
        }
      };

      fetchData(); // Call the API
    }
  }, [serviceData]);
  const validationSchema = (serviceId) => Yup.object({
    imei: serviceId === 48
      ? Yup.string().required(t('ErrorField6')) // Validation when serviceId is 48
      : Yup.string()
        .required(t("ErrorField4"))
        .length(15, t("ErrorField41")), // Default validation when serviceId is not 48
    email: Yup.string()
      .email(t("ErrorField51"))
      .required(t("ErrorField5")),
  });

  // Example usage of validationSchema in a form
  const schema = validationSchema(serviceData.serviceId);

  const showFreeSample = () => {
    setShow(!show)
  }
  const isValidIMEI = (imei) => {
    if (serviceData?.serviceId == 48) {
      return true
    }
    if (/^\d{15}$/.test(imei)) {
      const imeiDigits = imei.split("").map(Number);
      const checksum = imeiDigits.pop(); // Remove and save the last digit as the checksum

      const sum = imeiDigits.reduce((acc, digit, index) => {
        // Reverse the index calculation so we start doubling from the rightmost side
        const isSecondFromRight = (imeiDigits.length - index) % 2 === 1;
        const doubledDigit = isSecondFromRight ? digit * 2 : digit;

        // If doubling results in a number greater than 9, subtract 9 from it
        return acc + (doubledDigit > 9 ? doubledDigit - 9 : doubledDigit);
      }, 0);

      // Calculate the computed checksum digit
      const computedChecksum = (10 - (sum % 10)) % 10;

      return computedChecksum === checksum;
    }
    return false;
  };
  // Submit handler
  const handleSubmit = useCallback(
    async (values, { resetForm }) => {
      console.log('values: ', values);
      try {
        if (isValidIMEI(values.imei)) {
          const token = captchaRef.current.getValue();
          captchaRef.current.reset();
          if (!token) {
            return toast.error(t("Sorry!! Verify you're not a bot"));
          }

          startLoading();
          const valid_token = await verifyToken(token);
          console.log('valid_token: ', valid_token);
          setValidToken(valid_token);

          if (valid_token[0]?.success) {
            navigate('/service-review-order', { state: { formValue: values, productData: serviceData } });
            localStorage.setItem("state", JSON.stringify({ formValue: values, productData: serviceData }));
          } else {
            toast.error("Verification failed. Please try again.");
          }
        } else {
          toast.error("Invalid IMEI Number");
        }

      } catch (error) {
        console.error("Error in handleSubmit:", error);
        toast.error("Something went wrong");
      } finally {
        stopLoading();
      }
    },
    [serviceData, currentLanguageCode, startLoading, stopLoading, t]
  );

  // Verify reCAPTCHA token
  const verifyToken = useCallback(async (token) => {
    try {
      const response = await httpRequest.post(`${process.env.REACT_APP_API_URL}/verifyToken`, {
        reCAPTCHA_TOKEN: token,
      });
      toast.success(t("IEMIS1"));
      return [response];
    } catch (error) {
      console.error("Error verifying token:", error);
      return [];
    }
  }, [t]);

  return (
    <div>
      <section className="banner-ImeiBlacklist py-md-2 pt-3 pt-md-2 pb-0 position-relative px-2">
        <div className="container py-2 pt-md-4">
          <div className="row justify-content-center p-0 p-md-3">
            <div className="col-md-6 col-12 px-md-5">
              <h1 className="fw-bold display-5 mt-md-5">{serviceData.title}</h1>
              <p className="imeiblacklist text-justify m-0 pt-3">{serviceData.subtitle}</p>
              {serviceData?.heading &&<h2 className="fw-bold display-8 mt-md-5">{serviceData.heading}</h2>}
              {serviceData?.subheading &&<p className="imeiblacklist text-justify m-0 pt-3">{serviceData.subheading}</p>}
            </div>
            <div className="col-12 col-md-6 mt-4 mt-md-0 px-0">
              {!clientSecret && (
                <>
                  <Formik
                    initialValues={{ imei: "", email: "", callback: "" }}
                    validationSchema={schema}
                    onSubmit={handleSubmit}

                  >
                    {({ handleChange, values, handleBlur }) => (
                      <Form className="bg-white rounded-4 form-border p-1 p-md-4">
                        <div className="card unlock-card bg-transparent border-0 my-md-0 my-1">
                          <div className="card-body">
                            <h4 className="fw-semibold mb-4">{t('Ifmickf1')}</h4>
                            {serviceData && serviceData.serviceId == 48 ? (
                              <>
                                <div>
                                  <h6 className="fw-semibold small-font">{t('FormField61')}</h6>
                                  <div className="input-group mb-3 border rounded-input py-1 d-flex justify-content-between">
                                    <PhoneInput
                                      placeholder={t('FormField61')}
                                      country={currentLanguageCode === "en" ? "us" : "es"}
                                      autoFormat
                                      value={values.imei}
                                      onChange={(imei) => handleChange("imei")(imei)}
                                      onBlur={handleBlur("imei")}
                                    />
                                  </div>
                                </div>
                                <ErrorMessage name="imei" component="div" className="error-message ps-5 ms-4" />

                              </>

                            ) : (
                              <>

                                <div>
                                  <p className="mb-0 fw-semibold">{t('Icunp6')}</p>
                                  <div className="input-group border d-flex align-items-center p-2 rounded mb-3 bg-white w-100">
                                    <label className="input-group-text bg-transparent border-0 px-1" htmlFor="imei">
                                      <img loading="lazy" src="/images/ph_phone-call (1).svg" alt="" />
                                      <span className="mx-2mx-0 text-secondary"> | </span>
                                    </label>
                                    <Field
                                      type="text"
                                      className="form-control border-0 py-1"
                                      placeholder={t("IEMIPC1")}
                                      aria-label={t("IEMIPC1")}
                                      id="imei"
                                      name="imei"
                                      onChange={handleChange}
                                    />
                                  </div>
                                </div>
                                <ErrorMessage name="imei" component="div" className="error-message ps-5 ms-4" />
                              </>
                            )}
                            <div>
                              <p className="mb-0 fw-semibold">Email</p>
                              <div className="input-group border d-flex align-items-center p-2 rounded mb-3 bg-white w-100">
                                <label className="input-group-text bg-transparent border-0 px-1" htmlFor="email">
                                  <img loading="lazy" src="/images/at-sign2.svg" alt="" />
                                  <span className="mx-2mx-0 text-secondary"> | </span>
                                </label>
                                <Field
                                  type="email"
                                  className="form-control border-0 py-1"
                                  placeholder={t("IEMIPem1")}
                                  aria-label="email"
                                  id="email"
                                  name="email"
                                  onChange={handleChange}
                                />
                                <Field
                                  type="text"
                                  className="form-control border-0 py-1"
                                  placeholder={t("callback")}
                                  aria-label="callback"
                                  id="callback"
                                  name="callback"
                                  onChange={handleChange}
                                  style={{ display: "none" }}
                                />

                              </div>
                            </div>
                            <ErrorMessage name="email" component="div" className="error-message ps-5 ms-4" />
                            <button type="submit" className="btn btn-lg mt-md-0 mt-2 w-100 unlock-btn">
                              {t('UsefullB1')}
                            </button>

                            <Link to className="btn btn-lg mt-md-3 mt-2 py-3 px-0 w-100 border rounded fs-6 link1" onClick={showFreeSample}>
                              {show ? (
                                ` ${t('SSFSh1')}`
                              ) : (
                                `${t('SSFSh2')}`
                              )}
                            </Link>
                            {!show && (
                              <div className="row justify-content-center p-0 p-md-3 mt-3 mx-1 bg-light rounded ">
                                {freeSample && Object.entries(freeSample).map(([key, value], index) => (
                                  <div className="col-12 d-flex justify-content-between p-2 p-md-3 rounded" key={index}>
                                    <p className="mb-0 check-info-col1">{key}</p>
                                    <p className="mb-0 check-info-col2">{value}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                        </div>
                        <div>

                        </div>
                        <ReCAPTCHA
                          key={captchaKey}
                          className="recaptcha"
                          sitekey={"6LeLE9YqAAAAADOc0-cFTfmPBQRxgXbIGjXi-FQH"}
                          ref={captchaRef}
                          hl={currentLanguageCode}
                        />
                      </Form>



                    )}
                  </Formik>

                </>
              )}
            </div>
          </div>
        </div>
      </section>
      {serviceData.youtubeUrls &&
        <ServicePromotionVideos youtubeUrls={[serviceData.youtubeUrls]} title={serviceData.title} />
      }
    </div>
  );
};

export default DynamicCheckService;
